import React, { useState, useEffect } from 'react';
import { Building, Plus, Search, Filter, Eye, Edit, Trash2, MapPin, Phone, Mail, User } from 'lucide-react';
import { useHospitals } from '../../hooks/useApi';
import { useHospitalsCrud } from '../../hooks/useHospitalsCrud';
import { useNotification } from '../../hooks/useNotification';
import HospitalAddModal from './HospitalAddModal';
import HospitalEditModal from './HospitalEditModal';
import HospitalDetailsModal from './HospitalDetailsModal';
import HospitalDeleteModal from './HospitalDeleteModal';
import Notification from '../common/Notification';

const HospitalsList = () => {
  const { data: hospitals = [], loading, error, refetch } = useHospitals();
  const { addHospital, updateHospital, deleteHospital, isLoading } = useHospitalsCrud();
  const { notification, showNotification, hideNotification } = useNotification();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterProvince, setFilterProvince] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Extraire les valeurs uniques pour les filtres
  const provinces = [...new Set(hospitals.map(h => h.province))];
  const categories = [...new Set(hospitals.map(h => h.category))];
  const statuses = [...new Set(hospitals.map(h => h.status))];

  const filteredHospitals = hospitals.filter(hospital => {
    const matchesSearch = 
      hospital.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      hospital.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      hospital.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProvince = !filterProvince || hospital.province === filterProvince;
    const matchesCategory = !filterCategory || hospital.category === filterCategory;
    const matchesStatus = !filterStatus || hospital.status === filterStatus;
    return matchesSearch && matchesProvince && matchesCategory && matchesStatus;
  });

  const handleAddHospital = async (formData) => {
    try {
      await addHospital(formData);
      refetch();
      setShowCreateModal(false);
      showNotification('Hôpital ajouté avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de l\'ajout:', error);
      showNotification('Erreur lors de l\'ajout de l\'hôpital', 'error');
    }
  };

  const handleEditHospital = async (hospitalId, formData) => {
    try {
      await updateHospital(hospitalId, formData);
      refetch();
      setShowEditModal(false);
      setSelectedHospital(null);
      showNotification('Hôpital modifié avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      showNotification('Erreur lors de la modification de l\'hôpital', 'error');
    }
  };

  const handleDeleteHospital = async (hospitalId) => {
    try {
      await deleteHospital(hospitalId);
      refetch();
      setShowDeleteModal(false);
      setSelectedHospital(null);
      showNotification('Hôpital supprimé avec succès', 'success');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      showNotification('Erreur lors de la suppression de l\'hôpital', 'error');
    }
  };

  const openDetailsModal = (hospital) => {
    setSelectedHospital(hospital);
    setShowDetailsModal(true);
  };

  const openEditModal = (hospital) => {
    setSelectedHospital(hospital);
    setShowEditModal(true);
  };

  const openDeleteModal = (hospital) => {
    setSelectedHospital(hospital);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowDetailsModal(false);
    setShowEditModal(false);
    setShowCreateModal(false);
    setShowDeleteModal(false);
    setSelectedHospital(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Opérationnel':
        return 'bg-green-100 text-green-800';
      case 'Partiellement opérationnel':
        return 'bg-yellow-100 text-yellow-800';
      case 'En maintenance':
        return 'bg-blue-100 text-blue-800';
      case 'Fermé':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Hôpital général':
        return 'bg-blue-100 text-blue-800';
      case 'Hôpital universitaire':
        return 'bg-purple-100 text-purple-800';
      case 'Hôpital psychiatrique':
        return 'bg-pink-100 text-pink-800';
      case 'Hôpital spécialisé':
        return 'bg-cyan-100 text-cyan-800';
      case 'Centre hospitalier':
        return 'bg-teal-100 text-teal-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Hôpitaux</h1>
              <p className="text-gray-600 mt-2">Gérez les établissements de santé</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Nouvel Hôpital</span>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Chargement des hôpitaux...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Erreur de chargement</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        {!loading && !error && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Rechercher un hôpital..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <select
                  value={filterProvince}
                  onChange={(e) => setFilterProvince(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les provinces</option>
                  {provinces.map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Tous les statuts</option>
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 text-sm text-gray-500">
              {filteredHospitals.length} hôpital(s) trouvé(s)
            </div>
          </div>
        )}

        {/* Hospitals Grid */}
        {!loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredHospitals.map((hospital) => (
              <div
                key={hospital.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
              >
                {/* Header */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {hospital.name}
                      </h3>
                      <p className="text-sm text-gray-500">{hospital.code}</p>
                    </div>
                    <div className="ml-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(hospital.status)}`}>
                        {hospital.status}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 space-y-3">
                  {/* Catégorie */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Catégorie:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(hospital.category)}`}>
                      {hospital.category}
                    </span>
                  </div>

                  {/* Province */}
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                    <span>{hospital.city}, {hospital.province}</span>
                  </div>

                  {/* Zone de santé */}
                  {hospital.healthZone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Building className="h-4 w-4 text-gray-400 mr-2" />
                      <span>{hospital.healthZone.name}</span>
                    </div>
                  )}

                  {/* Directeur */}
                  {hospital.director && hospital.director.user && (
                    <div className="flex items-center text-sm text-gray-600">
                      <User className="h-4 w-4 text-gray-400 mr-2" />
                      <span>Dr. {hospital.director.user.name}</span>
                    </div>
                  )}

                  {/* Contact */}
                  {(hospital.phone || hospital.email) && (
                    <div className="space-y-1">
                      {hospital.phone && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Phone className="h-4 w-4 text-gray-400 mr-2" />
                          <span>{hospital.phone}</span>
                        </div>
                      )}
                      {hospital.email && (
                        <div className="flex items-center text-sm text-gray-600">
                          <Mail className="h-4 w-4 text-gray-400 mr-2" />
                          <span>{hospital.email}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Beds */}
                  {hospital.beds && (
                    <div className="text-sm text-gray-600">
                      <strong>{hospital.beds}</strong> lits disponibles
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-2">
                  <button
                    onClick={() => openDetailsModal(hospital)}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Voir les détails"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => openEditModal(hospital)}
                    className="p-2 text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(hospital)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && filteredHospitals.length === 0 && (
          <div className="text-center py-12">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun hôpital trouvé</h3>
            <p className="text-gray-500">Essayez de modifier vos critères de recherche</p>
          </div>
        )}
      </div>

      {/* Modals */}
      <HospitalAddModal
        isOpen={showCreateModal}
        onClose={closeModals}
        onSave={handleAddHospital}
        loading={isLoading}
      />

      <HospitalEditModal
        hospital={selectedHospital}
        isOpen={showEditModal}
        onClose={closeModals}
        onSave={handleEditHospital}
        loading={isLoading}
      />

      <HospitalDetailsModal
        hospital={selectedHospital}
        isOpen={showDetailsModal}
        onClose={closeModals}
      />

      <HospitalDeleteModal
        hospital={selectedHospital}
        isOpen={showDeleteModal}
        onClose={closeModals}
        onConfirm={handleDeleteHospital}
        loading={isLoading}
      />

      {/* Notification */}
      <Notification
        isVisible={notification.isVisible}
        onClose={hideNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </div>
  );
};

export default HospitalsList;
